import React, { useState, useEffect, useCallback, useRef } from "react";
import { cn } from "../../../lib/utils";
import {
  FileText,
  AlertTriangle,
  Clock,
  BarChart2,
  ChevronDown,
  ChevronRight,
  Copy,
  RefreshCw,
  Link,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
} from "lucide-react";
import { Button } from "../../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog";
import GatewayStatCard from "../components/GatewayStatCard";
import GatewayFilterBar from "../components/GatewayFilterBar";
import GatewayStatusBadge from "../components/GatewayStatusBadge";
import GatewayModuleBadge from "../components/GatewayModuleBadge";
import GatewaySeverityDot from "../components/GatewaySeverityDot";
import GatewayJsonViewer from "../components/GatewayJsonViewer";
import GatewayTimeline from "../components/GatewayTimeline";
import {
  GatewayAuditApi,
} from "../Api/gatewayApi";
import {
  formatTimestamp,
  formatDuration,
  durationClass,
  callerTypeLabel,
  copyToClipboard,
} from "../utils/gatewayUtils";
import { useToast } from "../../../../hooks/use-toast";
import type { AuditLogEntry, AuditStats, AuditFilters } from "../interfaces/gatewayInterfaces";


interface QueryParams {
  page: number;
  size: number;
  sort: string;
  module?: string | string[];
  eventType?: string | string[];
  status?: string | string[];
  severity?: string | string[];
  agentName?: string | string[];
  serverName?: string | string[];
  tokenType?: string | string[];
  userIdentity?: string | string[];
  since?: string;
  until?: string;
  sourceIp?: string;
  search?: string;
}

interface PagedResult {
  content: AuditLogEntry[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}


interface TimelineSlot {
  timestamp: Date;
  count: number;
}

function HourlyTimeline({ data }: { data: Array<{ timestamp?: string; hour?: string; count: number }> }) {
  const slots: TimelineSlot[] = [];
  const hourMap = new Map<number, number>();

  (data || []).forEach((d) => {
    const raw = d.timestamp || d.hour;
    if (raw) {
      const dt = new Date(raw);
      const key = new Date(
        dt.getFullYear(), dt.getMonth(), dt.getDate(), dt.getHours()
      ).getTime();
      hourMap.set(key, (hourMap.get(key) || 0) + (d.count || 0));
    }
  });

  const now = new Date();
  for (let i = 23; i >= 0; i--) {
    const slotDate = new Date(
      now.getFullYear(), now.getMonth(), now.getDate(), now.getHours() - i
    );
    slots.push({ timestamp: slotDate, count: hourMap.get(slotDate.getTime()) || 0 });
  }

  const maxCount = Math.max(...slots.map((s) => s.count), 1);
  const firstLabel = slots[0].timestamp.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
  const lastLabel = slots[slots.length - 1].timestamp.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });

  return (
    <div className="rounded-xl border border-border bg-card shadow-sm p-4">
      <p className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wider">
        Event Activity (Last 24 Hours)
      </p>
      <div className="flex items-end gap-0.5 h-20">
        {slots.map((s, i) => {
          const pct = s.count > 0 ? Math.max((s.count / maxCount) * 100, 4) : 2;
          const label = s.timestamp.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
          return (
            <div
              key={i}
              className="group relative flex-1 flex flex-col justify-end"
              style={{ height: "100%" }}
            >
              <div
                className={cn(
                  "w-full rounded-t-sm transition-all cursor-default",
                  s.count > 0
                    ? "bg-primary/50 hover:bg-primary/60"
                    : "bg-muted/50"
                )}
                style={{ height: `${pct}%` }}
              />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block z-10 whitespace-nowrap">
                <div className="bg-muted border border-border rounded px-2 py-1 text-[10px] text-foreground/80 shadow-lg">
                  {label}: {s.count} events
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between mt-1">
        <span className="text-[10px] text-muted-foreground/70">{firstLabel}</span>
        <span className="text-[10px] text-muted-foreground/70">{lastLabel}</span>
      </div>
    </div>
  );
}


function ExpandedLogRow({
  log,
  onShowCorrelation,
}: {
  log: AuditLogEntry;
  onShowCorrelation: (id: string) => void;
}) {
  const { toast } = useToast();
  const handleCopy = async (text: string) => {
    await copyToClipboard(text);
    toast({ title: "Copied to clipboard" });
  };
  const caller = callerTypeLabel(log.callerType);

  return (
    <div className="p-4 space-y-4 bg-muted/70 border-t border-border">
      <div className="space-y-2">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Identifiers</p>
        <div className="flex flex-wrap gap-3">
          {log.agentName && (
            <IdItem label="Agent" value={log.agentName} onCopy={handleCopy} />
          )}
          {log.sessionId && (
            <IdItem label="Session ID" value={log.sessionId} onCopy={handleCopy} />
          )}
          {log.correlationId && (
            <>
              <IdItem label="Correlation ID" value={log.correlationId} onCopy={handleCopy} />
              <button
                onClick={() => onShowCorrelation(log.correlationId!)}
                className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md border border-primary/30 bg-primary/10 text-primary hover:bg-primary/15 transition-colors"
              >
                <Link className="w-3 h-3" /> View Chain
              </button>
            </>
          )}
          {(log as any).requestId && (
            <IdItem label="Request ID" value={(log as any).requestId} onCopy={handleCopy} />
          )}
        </div>
      </div>

      {(log.callerType || (log as any).authMethod || log.humanUser || log.oauth2Client) && (
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Authentication</p>
          <div className="flex flex-wrap gap-3">
            {log.callerType && (
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-muted-foreground">Caller:</span>
                <span className={cn("px-1.5 py-0.5 rounded text-[10px] font-medium", caller.color)}>
                  {caller.label}
                </span>
              </div>
            )}
            {(log as any).authMethod && (
              <IdItem label="Auth Method" value={(log as any).authMethod} onCopy={handleCopy} />
            )}
            {log.humanUser && (
              <IdItem label="Human User" value={log.humanUser} onCopy={handleCopy} />
            )}
            {log.oauth2Client && (
              <IdItem label="OAuth2 Client" value={log.oauth2Client} onCopy={handleCopy} />
            )}
            {log.ipAddress && (
              <IdItem label="IP Address" value={log.ipAddress} onCopy={handleCopy} />
            )}
          </div>
        </div>
      )}

      {log.requestPayload && (
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Request Payload</p>
          <GatewayJsonViewer
            data={log.requestPayload}
            collapsed={false}
            maxHeight="320px"
          />
        </div>
      )}

      {log.responsePayload && (
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Response Payload</p>
          <GatewayJsonViewer
            data={log.responsePayload}
            collapsed={false}
            maxHeight="320px"
            unwrap
          />
        </div>
      )}

      {(log.status === "ERROR" || log.status === "FAILURE" || log.errorMessage) && (
        <div className="rounded-lg bg-red-500/10 border border-red-500/20 p-3 space-y-1">
          <p className="text-[10px] uppercase tracking-wider text-red-400 font-medium">Error Details</p>
          {(log as any).errorCode && (
            <p className="text-xs text-muted-foreground">
              Code: <span className="text-foreground/80">{(log as any).errorCode}</span>
            </p>
          )}
          {log.errorMessage && (
            <p className="text-xs text-red-300">{log.errorMessage}</p>
          )}
        </div>
      )}
    </div>
  );
}

function IdItem({
  label,
  value,
  onCopy,
}: {
  label: string;
  value: string;
  onCopy: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[10px] text-muted-foreground">{label}:</span>
      <span className="text-xs text-foreground/80 font-mono">{value}</span>
      <button
        onClick={() => onCopy(value)}
        className="p-0.5 rounded hover:bg-muted text-muted-foreground/70 hover:text-muted-foreground"
      >
        <Copy className="w-3 h-3" />
      </button>
    </div>
  );
}


export default function GatewayAuditPage() {
  const { toast } = useToast();

  const [stats, setStats] = useState<AuditStats | null>(null);
  const [timeline, setTimeline] = useState<any[]>([]);
  const [filterOptions, setFilterOptions] = useState<AuditFilters | null>(null);
  const [pagedResult, setPagedResult] = useState<PagedResult | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [tableLoading, setTableLoading] = useState(false);

  const [correlationId, setCorrelationId] = useState<string | null>(null);
  const [correlationEvents, setCorrelationEvents] = useState<any[]>([]);
  const [correlationLoading, setCorrelationLoading] = useState(false);

  const [params, setParams] = useState<QueryParams>({
    page: 0,
    size: 25,
    sort: "timestamp,desc",
  });

  const [filterValues, setFilterValues] = useState<Record<string, string | string[]>>({});

  useEffect(() => {
    Promise.all([
      GatewayAuditApi.stats().then(setStats).catch(console.warn),
      GatewayAuditApi.timeline(24).then(setTimeline).catch(console.warn),
      GatewayAuditApi.filters()
        .then((f) => setFilterOptions(f))
        .catch(console.warn),
    ]);
  }, []);

  const loadLogs = useCallback(async (p: QueryParams) => {
    setTableLoading(true);
    try {
      const result = await GatewayAuditApi.logs(p);
      setPagedResult(result);
    } catch (err: any) {
      toast({ title: "Failed to load audit logs", description: err?.message, variant: "destructive" });
    } finally {
      setTableLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadLogs(params);
  }, [params, loadLogs]);

  const filterFields = [
    {
      key: "module",
      label: "Module",
      type: "multiselect" as const,
      options: filterOptions?.modules || [],
    },
    {
      key: "eventType",
      label: "Event Type",
      type: "multiselect" as const,
      options: filterOptions?.eventTypes || [],
    },
    {
      key: "status",
      label: "Status",
      type: "multiselect" as const,
      options: filterOptions?.statuses || [],
    },
    {
      key: "severity",
      label: "Severity",
      type: "multiselect" as const,
      options: filterOptions?.severities || [],
    },
    {
      key: "agentName",
      label: "Agent",
      type: "multiselect" as const,
      options: filterOptions?.agents || [],
    },
    {
      key: "serverName",
      label: "Server",
      type: "multiselect" as const,
      options: filterOptions?.servers || [],
    },
    {
      key: "tokenType",
      label: "Token Type",
      type: "multiselect" as const,
      options: (filterOptions as any)?.tokenTypes || [],
    },
    {
      key: "userIdentity",
      label: "User Identity",
      type: "multiselect" as const,
      options: filterOptions?.humanUsers || [],
    },
    { key: "since", label: "Since", type: "date" as const },
    { key: "until", label: "Until", type: "date" as const },
    { key: "sourceIp", label: "IP Address", type: "ip" as const, placeholder: "e.g. 192.168.1.* or 10.0.0.5" },
    { key: "search", label: "Search", type: "text" as const, placeholder: "correlation, session, user..." },
  ];

  const handleFilterChange = (key: string, value: string | string[]) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
  };

  const pickArr = (key: string): string[] | undefined => {
    const v = filterValues[key];
    if (Array.isArray(v) && v.length > 0) return v;
    return undefined;
  };
  const pickStr = (key: string): string | undefined => {
    const v = filterValues[key];
    if (typeof v === "string" && v.trim()) return v;
    return undefined;
  };

  const handleApplyFilters = () => {
    const newParams: QueryParams = {
      ...params,
      page: 0,
      module: pickArr("module"),
      eventType: pickArr("eventType"),
      status: pickArr("status"),
      severity: pickArr("severity"),
      agentName: pickArr("agentName"),
      serverName: pickArr("serverName"),
      tokenType: pickArr("tokenType"),
      userIdentity: pickArr("userIdentity"),
      since: pickStr("since") ? new Date(pickStr("since")!).toISOString() : undefined,
      until: pickStr("until") ? new Date(pickStr("until")!).toISOString() : undefined,
      sourceIp: pickStr("sourceIp"),
      search: pickStr("search"),
    };
    setParams(newParams);
  };

  const handleClearFilters = () => {
    setFilterValues({});
    setParams({ page: 0, size: params.size, sort: params.sort });
  };

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleShowCorrelation = async (id: string) => {
    setCorrelationId(id);
    setCorrelationLoading(true);
    setCorrelationEvents([]);
    try {
      const events = await GatewayAuditApi.correlation(id);
      setCorrelationEvents(events || []);
    } catch (err: any) {
      toast({ title: "Failed to load correlation chain", description: err?.message, variant: "destructive" });
    } finally {
      setCorrelationLoading(false);
    }
  };

  const goToPage = (p: number) => setParams((prev) => ({ ...prev, page: p }));
  const changePageSize = (size: number) => setParams((prev) => ({ ...prev, page: 0, size }));

  const totalElements = pagedResult?.totalElements || 0;
  const totalPages = pagedResult?.totalPages || 0;
  const currentPage = pagedResult?.number || 0;
  const pageSize = pagedResult?.size || params.size;
  const from = totalElements === 0 ? 0 : currentPage * pageSize + 1;
  const to = Math.min((currentPage + 1) * pageSize, totalElements);

  const content = pagedResult?.content || [];

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold text-foreground flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Audit Log
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Security events, access logs, and compliance audit trail
          </p>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <GatewayStatCard
          icon={<FileText className="w-5 h-5" />}
          label="Total Events"
          value={(stats?.totalEvents || 0).toLocaleString()}
          colorClass="text-primary"
        />
        <GatewayStatCard
          icon={<AlertTriangle className="w-5 h-5" />}
          label="Errors"
          value={(stats?.errorCount || 0).toLocaleString()}
          subtitle={stats?.errorRate !== undefined ? `${stats.errorRate.toFixed(1)}% error rate` : undefined}
          colorClass="text-red-400"
        />
        <GatewayStatCard
          icon={<Clock className="w-5 h-5" />}
          label="Avg Duration"
          value={formatDuration(stats?.avgDurationMs)}
          colorClass="text-blue-400"
        />
        <div className="rounded-xl border border-border bg-card shadow-sm p-4">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2 font-medium">By Status</p>
          {stats?.byStatus && Object.keys(stats.byStatus).length > 0 ? (
            <div className="space-y-1">
              {Object.entries(stats.byStatus).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between">
                  <GatewayStatusBadge status={k} />
                  <span className="text-xs text-foreground/80 font-medium tabular-nums">
                    {(v as number).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground/70">No data</p>
          )}
        </div>
      </div>

      <HourlyTimeline data={timeline} />

      <GatewayFilterBar
        fields={filterFields}
        values={filterValues}
        onChange={handleFilterChange}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
      />

      <div className="rounded-xl border border-border overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border bg-card">
              <th className="w-8 px-3 py-2.5" />
              {[
                "Timestamp",
                "Agent",
                "Module",
                "Event Type",
                "Status",
                "Severity",
                "Server",
                "Capability",
                "Duration",
              ].map((h) => (
                <th
                  key={h}
                  className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider text-muted-foreground font-medium"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableLoading ? (
              <tr>
                <td colSpan={10} className="py-12 text-center">
                  <RefreshCw className="w-5 h-5 text-primary animate-spin mx-auto" />
                </td>
              </tr>
            ) : content.length === 0 ? (
              <tr>
                <td colSpan={10} className="py-12 text-center text-muted-foreground text-sm">
                  No audit events found — try adjusting your filters
                </td>
              </tr>
            ) : (
              content.map((log) => {
                const isExpanded = expandedRows.has(log.id);
                const isError = log.status === "ERROR" || log.status === "FAILURE";
                return (
                  <React.Fragment key={log.id}>
                    <tr
                      className={cn(
                        "border-b border-border hover:bg-muted/60 cursor-pointer transition-colors",
                        isError && "bg-red-500/5"
                      )}
                      onClick={() => toggleRow(log.id)}
                    >
                      <td className="px-3 py-2.5 text-muted-foreground">
                        {isExpanded ? (
                          <ChevronDown className="w-3.5 h-3.5" />
                        ) : (
                          <ChevronRight className="w-3.5 h-3.5" />
                        )}
                      </td>
                      <td className="px-3 py-2.5 font-mono text-muted-foreground whitespace-nowrap">
                        {formatTimestamp(log.timestamp)}
                      </td>
                      <td className="px-3 py-2.5 text-foreground/80 max-w-[100px] truncate">
                        {log.agentName || "-"}
                      </td>
                      <td className="px-3 py-2.5">
                        <GatewayModuleBadge module={log.module || ""} />
                      </td>
                      <td className="px-3 py-2.5 text-muted-foreground max-w-[140px] truncate">
                        {log.eventType || "-"}
                      </td>
                      <td className="px-3 py-2.5">
                        <GatewayStatusBadge status={log.status} />
                      </td>
                      <td className="px-3 py-2.5">
                        <GatewaySeverityDot severity={log.severity || "-"} />
                      </td>
                      <td className="px-3 py-2.5 text-muted-foreground max-w-[120px] truncate">
                        {log.serverConfigName || "-"}
                      </td>
                      <td className="px-3 py-2.5 text-muted-foreground max-w-[120px] truncate">
                        {log.capabilityName || "-"}
                      </td>
                      <td className={cn("px-3 py-2.5 tabular-nums font-medium", durationClass(log.duration))}>
                        {formatDuration(log.duration)}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={10} className="p-0">
                          <ExpandedLogRow
                            log={log}
                            onShowCorrelation={handleShowCorrelation}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>

        {!tableLoading && totalElements > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-card">
            <span className="text-xs text-muted-foreground">
              Showing {from}–{to} of {totalElements.toLocaleString()}
            </span>
            <div className="flex items-center gap-2">
              <button
                disabled={currentPage === 0}
                onClick={() => goToPage(currentPage - 1)}
                className="p-1 rounded-md border border-border text-muted-foreground hover:text-foreground/80 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>

              {(() => {
                const maxBtn = 5;
                const start = Math.max(0, Math.min(currentPage - 2, totalPages - maxBtn));
                const end = Math.min(totalPages, start + maxBtn);
                return Array.from({ length: end - start }, (_, i) => {
                  const p = start + i;
                  return (
                    <button
                      key={p}
                      onClick={() => goToPage(p)}
                      className={cn(
                        "w-7 h-7 rounded-md text-xs border transition-colors",
                        p === currentPage
                          ? "border-primary/50 bg-primary/10 text-primary"
                          : "border-border text-muted-foreground hover:text-foreground/80"
                      )}
                    >
                      {p + 1}
                    </button>
                  );
                });
              })()}

              <button
                disabled={currentPage >= totalPages - 1}
                onClick={() => goToPage(currentPage + 1)}
                className="p-1 rounded-md border border-border text-muted-foreground hover:text-foreground/80 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRightIcon className="w-3.5 h-3.5" />
              </button>

              <select
                value={pageSize}
                onChange={(e) => changePageSize(Number(e.target.value))}
                className="ml-2 h-7 bg-card border border-border rounded-md px-2 text-xs text-foreground/80 outline-none"
              >
                {[10, 25, 50].map((s) => (
                  <option key={s} value={s}>
                    {s} / page
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      <Dialog open={!!correlationId} onOpenChange={() => setCorrelationId(null)}>
        <DialogContent className="max-w-2xl bg-card border-border text-foreground">
          <DialogHeader>
            <DialogTitle className="text-base">Correlation Chain</DialogTitle>
          </DialogHeader>
          {correlationId && (
            <p className="text-xs text-muted-foreground">
              ID:{" "}
              <code className="text-primary font-mono">{correlationId}</code>
              {!correlationLoading && ` — ${correlationEvents.length} events`}
            </p>
          )}
          {correlationLoading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="w-5 h-5 text-primary animate-spin" />
            </div>
          ) : (
            <div className="max-h-[500px] overflow-auto">
              <GatewayTimeline events={correlationEvents} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
